import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Switch,
  Tooltip,
  Segmented
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import CustomResponseEditor, { validateCustomResponse } from '../../../shared/components/cdn/CustomResponseEditor';
import { useTranslation } from '../../../shared/i18n';

const { Option } = Select;

const PAGE_TYPES = [
  { value: 'waf_block', label: 'WAF 拦截页' },
  { value: 'rate_limit', label: '限速页 (429)' },
  { value: 'challenge', label: '人机验证页' },
  { value: 'maintenance', label: '维护页' },
  { value: 'http_error', label: 'HTTP 错误页' },
  { value: 'waiting_room', label: '排队页' }
];

// 本域名自定义响应 settings.custom_responses(覆盖账号级页面)
const CR_KINDS = [
  { key: 'waf_block', label: 'WAF 拦截', status: 403, presets: [403, 406, 451] },
  { key: 'rate_limit', label: '限速', status: 429, presets: [429, 503] },
  { key: 'origin_shield', label: '源站繁忙', status: 503, presets: [503, 429] },
  { key: 'waiting_room', label: '排队', status: 503, presets: [503, 200] }
];

const HTTP_STATUS_CODES = [400, 401, 403, 404, 408, 429, 500, 502, 503, 504];

const CONTENT_TYPES = [
  { value: 'text/html', label: 'HTML' },
  { value: 'text/plain', label: '纯文本' },
  { value: 'application/json', label: 'JSON' }
];

const parseSettings = (domainData) => {
  let s = domainData?.settings || {};
  if (typeof s === 'string') { try { s = JSON.parse(s); } catch (e) { s = {}; } }
  return s || {};
};

export default function ErrorPages() {
  // CustomResponseEditor 的校验函数需要 t
  const { t } = useTranslation();
  const { domainData, reloadDomain } = useOutletContext();
  const domainId = domainData?.id;

  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [form] = Form.useForm();
  const selectedPageType = Form.useWatch('page_type', form);

  // 维护模式(本域名)
  const errSettings = parseSettings(domainData)?.error_page || {};
  const [maintSaving, setMaintSaving] = useState(false);
  const saveMaintenance = async (on) => {
    setMaintSaving(true);
    try {
      const res = await cdnService.updateDomain({
        id: domainId,
        settings: { error_page: { ...errSettings, maintenance: on } }
      });
      if (res.success) {
        message.success(on ? '维护模式已开启' : '维护模式已关闭');
        reloadDomain && reloadDomain();
      } else {
        message.error(res.error || '保存失败');
      }
    } finally {
      setMaintSaving(false);
    }
  };
  const toggleMaintenance = (on) => {
    if (!on) return saveMaintenance(false);
    Modal.confirm({
      title: '开启维护模式？',
      content: '该域名所有请求将返回维护页',
      okText: '确定',
      cancelText: '取消',
      onOk: () => saveMaintenance(true)
    });
  };

  // 本域名自定义响应
  const [crMap, setCrMap] = useState({});
  const [crKind, setCrKind] = useState('waf_block');
  const [crSaving, setCrSaving] = useState(false);
  useEffect(() => {
    const m = parseSettings(domainData)?.custom_responses;
    setCrMap(m && typeof m === 'object' && !Array.isArray(m) ? m : {});
  }, [domainData]);
  const saveCustomResponses = async () => {
    const out = {};
    for (const k of CR_KINDS) {
      const cr = crMap[k.key];
      if (!cr || !String(cr.body || '').length) continue;
      const err = validateCustomResponse(cr, t);
      if (err) {
        message.error(`${k.label}: ${err}`);
        return;
      }
      const item = { body: cr.body };
      if (cr.status) item.status = Number(cr.status);
      if (cr.content_type && String(cr.content_type).trim()) item.content_type = String(cr.content_type).trim();
      out[k.key] = item;
    }
    setCrSaving(true);
    try {
      const res = await cdnService.updateDomain({ id: domainId, settings: { custom_responses: out } });
      if (res.success) {
        message.success('已保存');
        reloadDomain && reloadDomain();
      } else {
        message.error(res.error || '保存失败');
      }
    } catch (e) {
      message.error('保存失败');
    } finally {
      setCrSaving(false);
    }
  };
  const crCur = CR_KINDS.find((k) => k.key === crKind) || CR_KINDS[0];

  const loadPages = useCallback(async () => {
    if (!domainId) return;
    setLoading(true);
    try {
      const response = await cdnService.getErrorPages(domainId);
      if (response.success) {
        setPages(response.data || []);
      } else {
        message.error(response.error || '加载错误页面失败');
      }
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const handleAdd = () => {
    form.resetFields();
    setEditingPage(null);
    setModalVisible(true);
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    form.setFieldsValue({
      page_type: page.page_type,
      name: page.name,
      status_code: page.status_code,
      content_type: page.content_type || 'text/html',
      content: page.content
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await cdnService.deleteErrorPage(domainId, id);
      if (response.success) {
        message.success('已删除');
        loadPages();
      } else {
        message.error(response.error || '删除失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTemplate = async () => {
    const pageType = form.getFieldValue('page_type');
    if (!pageType) {
      message.warning('请先选择页面类型');
      return;
    }
    setTemplateLoading(true);
    try {
      const response = await cdnService.getErrorPageTemplates(domainId);
      if (response.success && response.data?.[pageType]) {
        form.setFieldValue('content', response.data[pageType].template);
      } else {
        message.error('加载模板失败');
      }
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        page_type: values.page_type,
        name: values.name,
        content_type: values.content_type || 'text/html',
        content: values.content
      };
      if (values.page_type === 'http_error') {
        payload.status_code = values.status_code;
      }
      const response = await cdnService.saveErrorPage(domainId, payload);
      if (response.success) {
        message.success(editingPage ? '已更新' : '已创建');
        setModalVisible(false);
        loadPages();
      } else {
        message.error(response.error || '操作失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const typeLabel = (type) => PAGE_TYPES.find((p) => p.value === type)?.label || type;

  const columns = [
    { title: '类型', dataIndex: 'page_type', render: (type) => <Tag color="blue">{typeLabel(type)}</Tag> },
    { title: '名称', dataIndex: 'name' },
    { title: '状态码', dataIndex: 'status_code', render: (code) => (code ? <Tag color="orange">{code}</Tag> : '-') },
    { title: '内容类型', dataIndex: 'content_type', render: (ct) => ct || 'text/html' },
    { title: '更新时间', dataIndex: 'updated_at' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="删除该错误页面？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <ExclamationCircleOutlined />
            自定义错误页面
            <Tooltip title={`账号级，作用于用户 #${domainData?.user_id} 的全部域名`}>
              <Tag>账号级</Tag>
            </Tooltip>
          </Space>
        }
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加错误页面</Button>}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Tooltip title="开启后本域名所有请求返回维护页 (503)">
              <span>维护模式（本域名）</span>
            </Tooltip>
            <Switch
              checked={!!errSettings.maintenance}
              loading={maintSaving}
              onChange={toggleMaintenance}
              disabled={!domainId}
            />
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={pages}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: '未设置，使用默认页面' }}
        />
      </Card>

      {domainId && (
        <Card
          title="本域名自定义响应"
          style={{ marginTop: 16 }}
          extra={<Button type="primary" loading={crSaving} onClick={saveCustomResponses}>保存</Button>}
        >
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <Segmented
              value={crKind}
              onChange={setCrKind}
              options={CR_KINDS.map((k) => ({
                value: k.key,
                label: (
                  <span>
                    {k.label}
                    {String(crMap[k.key]?.body || '').length > 0 && <Tag color="green" style={{ marginLeft: 6, marginRight: 0 }}>自定义</Tag>}
                  </span>
                )
              }))}
            />
          </div>
          <Form layout="vertical" key={crKind}>
            <CustomResponseEditor
              value={crMap[crKind]}
              onChange={(v) => setCrMap((prev) => ({ ...prev, [crKind]: v }))}
              defaultStatus={crCur.status}
              statusPresets={crCur.presets}
              waitingRoom={crKind === 'waiting_room'}
            />
          </Form>
        </Card>
      )}

      <Modal
        title={editingPage ? '编辑错误页面' : '添加错误页面'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={800}
        style={{ top: 40 }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ content_type: 'text/html' }}>
          <Form.Item name="page_type" label="页面类型" rules={[{ required: true, message: '请选择页面类型' }]}>
            <Select placeholder="选择页面类型" disabled={!!editingPage}>
              {PAGE_TYPES.map((p) => <Option key={p.value} value={p.value}>{p.label}</Option>)}
            </Select>
          </Form.Item>

          {selectedPageType === 'http_error' && (
            <Form.Item name="status_code" label="HTTP 状态码" rules={[{ required: true, message: '请选择状态码' }]}>
              <Select placeholder="选择状态码">
                {HTTP_STATUS_CODES.map((code) => <Option key={code} value={code}>{code}</Option>)}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="例如：自定义 404 页面" />
          </Form.Item>

          <Form.Item name="content_type" label="内容类型">
            <Select>
              {CONTENT_TYPES.map((ct) => <Option key={ct.value} value={ct.value}>{ct.label}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item
            name="content"
            label={
              <Space>
                <span>页面内容</span>
                <Button size="small" loading={templateLoading} onClick={handleLoadTemplate}>加载模板</Button>
              </Space>
            }
            rules={[{ required: true, message: '请输入页面内容' }]}
          >
            <Input.TextArea rows={12} style={{ fontFamily: 'monospace', fontSize: 13 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
